/**
 * Reading the books out of the local database.
 *
 * The journal entries Dexie holds are already the shape the report functions expect, so this is
 * a load and not a translation. The reports themselves live in `packages/core` and run
 * unchanged on the server — which is the whole point: the owner's phone and the office must
 * never be able to produce two different profit figures.
 */

import { useCallback, useMemo, useState } from 'react';
import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { ACCOUNTS, cuadres, movimientos, type Cuadre, type JournalEntry, type Venta } from '@abastece/core';
import {
  DEFAULT_SETTINGS,
  cordobasToUsd,
  formatMoney,
  fxRate,
  hoy,
  sumarDias,
  type Centavos,
  type FormatMoneyOptions,
  type FxRate,
} from '@abastece/shared';
import { db, type AsientoRow, type SaldoDiario } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import type { Clave } from '../i18n/index.js';

const comoEntrada = (f: AsientoRow): JournalEntry => ({
  eventId: f.eventId,
  fecha: f.fecha,
  concepto: f.concepto,
  lineas: f.lineas as JournalEntry['lineas'],
});

export async function asientosLocales(): Promise<JournalEntry[]> {
  return (await db.asientos.toArray()).map(comoEntrada);
}

/** The entries dated inside a range, found by date: the accountant's month, not the whole journal. */
export async function asientosDelRango(r: { desde?: string; hasta?: string }): Promise<JournalEntry[]> {
  if (!r.desde && !r.hasta) return asientosLocales();
  const filas = await db.asientos
    .where('fecha')
    .between(r.desde ?? Dexie.minKey, r.hasta ?? Dexie.maxKey, true, true)
    .toArray();
  return filas.map(comoEntrada);
}

/** One account's day (`SaldoDiario`) as the one-line entry the report functions read. */
const comoAsiento = (d: SaldoDiario): JournalEntry => ({
  eventId: '',
  fecha: d.fecha,
  concepto: '',
  lineas: [{ cuenta: d.cuenta, debe: d.debe as Centavos, haber: d.haber as Centavos }],
});

/**
 * The journal a report of one period needs, and no more: the period's own entries, and before
 * it each account's days added up. Every report here adds lines by account and by date, so a
 * day's total stands in for that day's entries: what came before weighs in the balances as it
 * does in the whole journal, and the period is there entry by entry. At 90 days of trading the
 * days are 542 rows against 46,729 entries: before a period, each day of history adds some six
 * rows to what is read instead of five hundred entries.
 */
export async function libroDelPeriodo(r: { desde?: string; hasta?: string }): Promise<JournalEntry[]> {
  if (!r.desde) return asientosDelRango(r);
  const [antes, periodo] = await Promise.all([
    db.saldosDiarios.where('fecha').below(r.desde).toArray(),
    asientosDelRango(r),
  ]);
  return [...antes.map(comoAsiento), ...periodo];
}

/**
 * Each account's days inside a range, as one-line entries: for a report that only adds the
 * period's lines by account, such as the income statement, the same answer as its entries at a
 * hundredth of the reading. «Análisis» compares a year with the one before.
 */
export async function diasDelRango(r: { desde: string; hasta: string }): Promise<JournalEntry[]> {
  return (await db.saldosDiarios.where('fecha').between(r.desde, r.hasta, true, true).toArray()).map(comoAsiento);
}

/** The sales dated inside a range, found by date. The reports never look outside it. */
export function ventasDelPeriodo(r: { desde?: string; hasta?: string }) {
  if (!r.desde && !r.hasta) return db.ventas.toArray();
  return db.ventas
    .where('fecha')
    .between(r.desde ?? Dexie.minKey, r.hasta ?? Dexie.maxKey, true, true)
    .toArray();
}

/**
 * When each of the period's sales was rung: the instant on its record in the log. A sale keeps its
 * day and not its hour; the record the till wrote has both. A record the server turned away has no
 * sale standing behind it, and the same sale sent twice counts once.
 */
export async function momentosDeVentas(ventas: readonly Venta[], t: { desde: string; hasta: string }): Promise<string[]> {
  const ids = new Set(ventas.filter((v) => !v.anulada && v.fecha >= t.desde && v.fecha <= t.hasta).map((v) => v.id));
  if (ids.size === 0) return [];
  const momentos = new Map<string, string>();
  await db.eventos
    .where('type')
    .equals('venta.registrada')
    .each((e) => {
      const id = (e.payload as { ventaId?: unknown } | undefined)?.ventaId;
      if (typeof id === 'string' && ids.has(id) && !momentos.has(id)) momentos.set(id, e.clientTs);
    });
  return [...momentos.values()];
}

/**
 * Each account with a detail of its own against that detail, on this device: what the books say
 * against what the clients, the purchases and the shelves add up to (`reportes/cuadres.ts` in core;
 * the server draws the same lines from its own tables, `/api/estado/cuadres`).
 */
export async function cuadresLocales(): Promise<Cuadre[]> {
  const [dias, clientes, compras, gastos, existencias, sesiones, enCola, recargas, abonos, saldos, empleados, planillas, pagos] =
    await Promise.all([
      // Each account's balance and its number of lines, from its days: not every entry again.
      db.saldosDiarios.toArray(),
      db.clientes.toArray(),
      db.compras.toArray(),
      db.gastos.toArray(),
      db.existencias.toArray(),
      db.sesiones.toArray(),
      // Only the sales with a payment still waiting: found by index, not by reading every sale.
      db.ventas.where('enCola').equals(1).toArray(),
      db.recargas.toArray(),
      // An abono's payment still waiting in the queue (`estadoPago`).
      db.abonos.filter((a) => a.estadoPago === 'pendiente').toArray(),
      db.saldos.toArray(),
      db.empleados.toArray(),
      db.planillas.toArray(),
      db.pagosCotizaciones.toArray(),
    ]);
  const lineas = new Map<string, number>();
  for (const d of dias) lineas.set(d.cuenta, (lineas.get(d.cuenta) ?? 0) + d.lineas);
  return cuadres({
    saldos: movimientos(dias.map(comoAsiento)),
    lineasInventario: lineas,
    clientes,
    compras,
    gastos,
    existencias,
    sesiones,
    ventas: enCola,
    recargas,
    abonos,
    saldosTelefonicos: saldos,
    empleados,
    planillas,
    pagosCotizaciones: pagos,
  });
}

/**
 * What the period's profit left in the fiado notebook: how much what clients owe grew over the
 * period (fiado given less abonos, returns and what was given up on), and what they owe at its
 * end. A sale on fiado is profit the day it is rung and money only when it is paid, so without
 * this «¿Cuánto gané?» shows money the owner does not have yet. What was owed from before the
 * shop started here is left out of the growth: it went against Capital, not profit.
 */
export function fiadoDelPeriodo(
  asientos: readonly JournalEntry[],
  rango: { desde?: string; hasta?: string },
): { crecio: Centavos; debenAlFinal: Centavos } {
  const cxc = ACCOUNTS.CUENTAS_POR_COBRAR.code;
  const capital = ACCOUNTS.CAPITAL.code;
  let crecio = 0;
  let deben = 0;
  for (const a of asientos) {
    if (rango.hasta && a.fecha > rango.hasta) continue;
    let movio = 0;
    for (const l of a.lineas) if (l.cuenta === cxc) movio += l.debe - l.haber;
    deben += movio;
    if (rango.desde && a.fecha < rango.desde) continue;
    if (!a.lineas.some((l) => l.cuenta === capital)) crecio += movio;
  }
  return { crecio: crecio as Centavos, debenAlFinal: deben as Centavos };
}

export interface Periodo {
  desde: string;
  hasta: string;
  etiqueta: Clave;
}

/**
 * The periods an owner actually asks about, counted from a day at the shop.
 *
 * Worked out on the date itself. They used to take the month from the phone's clock and the
 * day from UTC, and on the last evening of a month the two disagreed about which month it was.
 */
export function periodos(dia = hoy()): Periodo[] {
  const inicioMes = `${dia.slice(0, 7)}-01`;
  const finMesPasado = sumarDias(inicioMes, -1);
  const inicioMesPasado = `${finMesPasado.slice(0, 7)}-01`;

  return [
    { desde: dia, hasta: dia, etiqueta: 'periodo.hoy' },
    // Yesterday, after today (the owner, 2026-10-14).
    { desde: sumarDias(dia, -1), hasta: sumarDias(dia, -1), etiqueta: 'periodo.ayer' },
    { desde: sumarDias(dia, -29), hasta: dia, etiqueta: 'periodo.ultimos30' },
    { desde: inicioMes, hasta: dia, etiqueta: 'periodo.esteMes' },
    { desde: inicioMesPasado, hasta: finMesPasado, etiqueta: 'periodo.mesPasado' },
    { desde: `${dia.slice(0, 4)}-01-01`, hasta: dia, etiqueta: 'periodo.esteAnio' },
    { desde: '', hasta: '', etiqueta: 'periodo.todo' },
  ];
}

/** The named periods of «Contabilidad», as its address keeps them: `periodos()`'s, by name. */
export const PERIODOS_CONTABILIDAD = ['hoy', 'ayer', 'ultimos30', 'esteMes', 'mesPasado', 'esteAnio', 'todo'] as const;
export type PeriodoContabilidad = (typeof PERIODOS_CONTABILIDAD)[number];
export const VISTAS_CONTABILIDAD = ['resultados', 'balance', 'caja', 'utilidad'] as const;
export type VistaContabilidad = (typeof VISTAS_CONTABILIDAD)[number];

/**
 * What «Contabilidad» shows, kept in its address: the tab, and a named period or any days. It was
 * the page's own, and «Exportar» and back, or «Ver en Contabilidad» from «Análisis», opened «Este
 * mes» whatever had been read (2026-10-13). What it opens on by itself, the results this month,
 * says nothing.
 */
export interface BusquedaContabilidad {
  vista?: Exclude<VistaContabilidad, 'resultados'>;
  periodo?: Exclude<PeriodoContabilidad, 'esteMes'>;
  desde?: string;
  hasta?: string;
}

const esDia = (x: unknown): x is string => typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x) && !Number.isNaN(Date.parse(`${x}T00:00:00Z`));

export function busquedaDeContabilidad(s: Record<string, unknown>): BusquedaContabilidad {
  const vista = VISTAS_CONTABILIDAD.find((v) => v === s['vista']);
  const nombrado = PERIODOS_CONTABILIDAD.find((p) => p === s['periodo']);
  const dias = esDia(s['desde']) && esDia(s['hasta']) && s['desde'] <= s['hasta'] ? { desde: s['desde'], hasta: s['hasta'] } : null;
  return {
    ...(vista && vista !== 'resultados' ? { vista } : {}),
    ...(dias ?? (nombrado && nombrado !== 'esteMes' ? { periodo: nombrado } : {})),
  };
}

/** Drops the empty bounds so "Todo" means no filter rather than a range of empty strings. */
export function comoRango(p: Pick<Periodo, 'desde' | 'hasta'>): { desde?: string; hasta?: string } {
  return { ...(p.desde ? { desde: p.desde } : {}), ...(p.hasta ? { hasta: p.hasta } : {}) };
}

/** What «Contabilidad» and «Análisis» read their figures in: córdobas, as the books are kept, or dollars. */
export type MonedaDeLosLibros = 'NIO' | 'USD';

const CLAVE_MONEDA = 'abastece.libros.moneda';

/**
 * The currency the books were last read in on this device: córdobas unless dollars were picked
 * here. It belongs to the device, like the receipt's paper: an owner who reads the books in dollars
 * is not made to pick them every time. One choice for «Contabilidad» and «Análisis»: dollars picked
 * on either open the other in dollars too.
 */
export function monedaDeLosLibros(): MonedaDeLosLibros {
  try {
    return localStorage.getItem(CLAVE_MONEDA) === 'USD' ? 'USD' : 'NIO';
  } catch {
    return 'NIO';
  }
}

export function elegirMonedaDeLosLibros(moneda: MonedaDeLosLibros): void {
  try {
    if (moneda === 'USD') localStorage.setItem(CLAVE_MONEDA, 'USD');
    else localStorage.removeItem(CLAVE_MONEDA);
  } catch {
    // A device that refuses storage opens the books in córdobas each time. Harmless.
  }
}

/** An amount as «Contabilidad» writes it, `formatMoney`'s options included. */
export type DineroDeLosLibros = (c: Centavos, opts?: FormatMoneyOptions) => string;

/**
 * How «Contabilidad» writes the books' córdobas: as they are, or in dollars (the owner, 2026-10-12:
 * «a radio button to change the values from cordobas to dollars») at the rate the books are kept at,
 * the official one. Each figure is turned and rounded to the cent on its own, so a total can be a
 * cent off the sum of its lines. Signed where `formatMoney` signs: «-US$ 12.50».
 */
export function dineroDeLosLibros(moneda: MonedaDeLosLibros, tasa: FxRate): DineroDeLosLibros {
  if (moneda === 'NIO') return formatMoney;
  return (c, opts = {}) => {
    const usd = cordobasToUsd(c, tasa);
    const signo = usd < 0 ? '-' : opts.signed && usd > 0 ? '+' : '';
    return `${signo}${opts.symbol === false ? '' : 'US$ '}${formatMoney(Math.abs(usd) as Centavos, { symbol: false })}`;
  };
}

/** Dollars as `dineroDeLosLibros` writes them, for what was in dollars to begin with: «US$ 1,250.00». */
export const dolaresDeLosLibros = (usd: number): string =>
  `${usd < 0 ? '-' : ''}US$ ${formatMoney(Math.abs(usd) as Centavos, { symbol: false })}`;

/**
 * A page of the owner's figures read in córdobas or in dollars: the phone's one choice
 * (`monedaDeLosLibros`), how it writes them (`dineroDeLosLibros`) and the official rate it is at.
 * «Contabilidad» and «Análisis» first; «Control» and «Gastos», with its lists of payments and of the
 * owner's money, since 2026-10-13 (the owner: «do a comprehensive analysis of what other modules
 * could benefit from the same toggle and add it»); «Fiado», the owner's view of it, since 2026-10-14;
 * «Por comprar» since 2026-10-16 («also give that page a cordobas/dollar toggle»). Nothing typed
 * changes: a form is in córdobas.
 */
export function useMonedaDeLosLibros(): {
  moneda: MonedaDeLosLibros;
  elegir: (m: MonedaDeLosLibros) => void;
  dinero: DineroDeLosLibros;
  tasa: FxRate;
} {
  const [moneda, setMoneda] = useState<MonedaDeLosLibros>(monedaDeLosLibros);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const tasa = fxRate(config.fxTasaOficial);
  const dinero = useMemo(() => dineroDeLosLibros(moneda, tasa), [moneda, tasa]);
  const elegir = useCallback((m: MonedaDeLosLibros) => {
    setMoneda(m);
    elegirMonedaDeLosLibros(m);
  }, []);
  return { moneda, elegir, dinero, tasa };
}
